import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, MapPin, Clock, Columns2, ImageOff } from "lucide-react";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const Unavailable = ({ photo, t, className }) => (
  <div data-testid="wo-viewer-photo-unavailable" className={cn("flex flex-col items-center justify-center gap-2 rounded-[8px] bg-white/5 p-8 text-center", className)}>
    <ImageOff className="h-8 w-8 text-white/50" />
    <p className="text-[13px] text-white/70">{t("wo.evidence.unavailable")}</p>
    <p className="text-[11.5px] text-white/40">
      {formatDateTime(photo.captured_at || photo.created_at)}{photo.uploaded_by_nombre ? ` · ${photo.uploaded_by_nombre}` : ""}
    </p>
  </div>
);

const Meta = ({ photo, t }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-ink-tertiary">
    <span>{t(`wo.evidence.types.${photo.tipo}`)} · {t("wo.evidence.cycle")} {photo.ciclo}</span>
    <span className="inline-flex items-center gap-1">
      <Clock className="h-3 w-3" />
      {t("wo.evidence.captured")}: {formatDateTime(photo.captured_at || photo.created_at)}
    </span>
    {photo.gps && (
      <span className="inline-flex items-center gap-1">
        <MapPin className="h-3 w-3" />
        {photo.gps.lat}, {photo.gps.lon}
      </span>
    )}
    <span>{t("wo.evidence.uploaded_by")}: {photo.uploaded_by_nombre || "—"}</span>
  </div>
);

export const PhotoViewer = ({ photos, index, onIndexChange, onClose, compare, onToggleCompare }) => {
  const { t } = useT();
  const [zoom, setZoom] = useState(1);
  const [broken, setBroken] = useState(() => new Set());
  const markBroken = (id) => setBroken((s) => new Set(s).add(id));
  const photo = photos[index];

  useEffect(() => setZoom(1), [index, compare]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndexChange((index + 1) % photos.length);
      if (e.key === "ArrowLeft") onIndexChange((index - 1 + photos.length) % photos.length);
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.25));
      if (e.key === "-") setZoom((z) => Math.max(1, z - 0.25));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, photos.length, onClose, onIndexChange]);

  if (!photo && !compare) return null;

  const antes = photos.filter((p) => p.tipo === "antes");
  const despues = photos.filter((p) => p.tipo === "despues" || p.tipo === "correccion");

  return (
    <div
      data-testid="wo-photo-viewer"
      className="fixed inset-0 z-50 flex flex-col bg-ink"
    >
      <div className="flex items-center gap-3 px-5 py-3 text-white">
        <span className="text-[13px] font-medium">
          {compare ? t("wo.evidence.compare") : `${index + 1} / ${photos.length}`}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            data-testid="wo-viewer-compare"
            onClick={onToggleCompare}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-[12.5px] transition-colors",
              compare ? "bg-white text-ink" : "bg-white/10 text-white hover:bg-white/20"
            )}
          >
            <Columns2 className="h-3.5 w-3.5" /> {t("wo.evidence.compare")}
          </button>
          {!compare && (
            <>
              <button
                data-testid="wo-viewer-zoom-out"
                onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-white/10 hover:bg-white/20"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                data-testid="wo-viewer-zoom-in"
                onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-white/10 hover:bg-white/20"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </>
          )}
          <button
            data-testid="wo-viewer-close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-white/10 hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {compare ? (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 px-5 pb-5">
          {[
            { label: t("wo.evidence.types.antes"), list: antes },
            { label: t("wo.evidence.types.despues"), list: despues },
          ].map((side) => (
            <div key={side.label} className="flex min-h-0 flex-col">
              <p className="pb-2 text-[12px] uppercase tracking-[0.06em] text-ink-tertiary">{side.label}</p>
              <div className="flex min-h-0 flex-1 items-center justify-center rounded-[8px] bg-black/40">
                {side.list[0] ? (
                  broken.has(side.list[0].id) ? (
                    <Unavailable photo={side.list[0]} t={t} />
                  ) : (
                    <img
                      src={side.list[0].url}
                      alt={side.label}
                      onError={() => markBroken(side.list[0].id)}
                      className="max-h-full max-w-full object-contain"
                    />
                  )
                ) : (
                  <p className="p-6 text-center text-[12.5px] text-ink-tertiary">
                    {t("wo.evidence.compare_missing")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto px-16">
            <button
              onClick={() => onIndexChange((index - 1 + photos.length) % photos.length)}
              data-testid="wo-viewer-prev"
              className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <img
              src={photo.url}
              alt=""
              style={{ transform: `scale(${zoom})`, display: broken.has(photo.id) ? "none" : undefined }}
              onError={() => markBroken(photo.id)}
              className="max-h-full max-w-full object-contain transition-transform duration-200"
            />
            {broken.has(photo.id) && <Unavailable photo={photo} t={t} className="absolute" />}
            <button
              onClick={() => onIndexChange((index + 1) % photos.length)}
              data-testid="wo-viewer-next"
              className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="px-5 py-3">
            <Meta photo={photo} t={t} />
          </div>
        </>
      )}
    </div>
  );
};
